import type { ComponentProps } from 'react';
import ReactMarkdown from 'react-markdown';

import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import remarkGfm from 'remark-gfm';

import { SparksAgentService } from '@/components/composite/SparksAgent/services/SparksAgentService';

import { useAgentMessageTypewriter } from '@/components/composite/SparksAgent/hooks/useAgentMessageTypewriter';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';
import { useCopyToClipboard } from '@/utils/hooks/utils/useCopyToClipboard';

import Icon from '@/components/base/Icon';

import type { SparksMessage } from './SparksConversation';

type SparksMessageBubbleProps = {
    message: SparksMessage;
    /** When true (agent only), reveal text with a typing-style animation once. */
    animateTyping?: boolean;
    onTypingComplete?: () => void;
};

const LIST_CLASS_NAMES =
    'min-w-0 list-outside space-y-1 break-words py-1 pl-5 [&_li>ol]:mt-1 [&_li>ul]:mt-1 [&_li>ol]:pl-4 [&_li>ul]:pl-4';

const MARKDOWN_COMPONENTS = {
    p: ({ children }) => (
        <p className="mb-0 mt-2 min-w-0 break-words leading-5 first:mt-0">{children}</p>
    ),
    ul: ({ children }) => (
        <ul
            className={classNames(
                LIST_CLASS_NAMES,
                'marker:text-icoGray-600 list-disc dark:marker:text-icoDarkMode-wolf'
            )}>
            {children}
        </ul>
    ),
    ol: ({ children }) => (
        <ol
            className={classNames(
                LIST_CLASS_NAMES,
                'marker:text-icoGray-800 list-decimal font-GMedium dark:marker:text-white'
            )}>
            {children}
        </ol>
    ),
    li: ({ children }) => (
        <li className="min-w-0 pl-0.5 text-inherit [&>p+p]:!mt-1 [&>p]:!my-0">{children}</li>
    ),
    h1: ({ children }) => (
        <h1 className="mb-1 mt-4 font-GSemiBold text-sm text-icoBlack first:mt-0 dark:text-white">
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 className="mb-1 mt-4 font-GSemiBold text-sm text-icoBlack first:mt-0 dark:text-white">
            {children}
        </h2>
    ),
    h3: ({ children }) => (
        <h3 className="mb-1 mt-3 font-GSemiBold text-xs text-icoBlack first:mt-0 dark:text-white">
            {children}
        </h3>
    ),
    h4: ({ children }) => (
        <h4 className="mb-0.5 mt-2 font-GSemiBold text-xs text-icoBlack first:mt-0 dark:text-white">
            {children}
        </h4>
    ),
    strong: ({ children }) => <strong className="font-GSemiBold">{children}</strong>,
    hr: () => (
        <hr className="border-icoGray-200 my-3 border-0 border-t dark:border-icoDarkMode-beton" />
    ),
    pre: ({ children }) => (
        <pre className="border-icoGray-200 bg-icoGray-50 font-mono my-3 max-w-full overflow-x-auto rounded-lg border p-3 text-[11px] tabular-nums leading-relaxed text-icoBlack dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack dark:text-white">
            {children}
        </pre>
    ),
    table: ({ children }) => (
        <div className="border-icoGray-200 my-3 max-w-full overflow-x-auto rounded-lg border bg-white shadow-sm dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack">
            <table className="w-full min-w-max border-collapse text-left text-[11px] leading-snug text-icoBlack dark:text-white">
                {children}
            </table>
        </div>
    ),
    thead: ({ children }) => (
        <thead className="border-icoGray-200 border-b bg-icoGray-100 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
            {children}
        </thead>
    ),
    tbody: ({ children }) => (
        <tbody className="divide-y divide-icoGray-100 dark:divide-icoDarkMode-beton">
            {children}
        </tbody>
    ),
    tfoot: ({ children }) => (
        <tfoot className="border-icoGray-200 bg-icoGray-50 border-t dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
            {children}
        </tfoot>
    ),
    tr: ({ children }) => (
        <tr className="even:bg-icoGray-50/80 align-top dark:even:bg-white/[0.02]">{children}</tr>
    ),
    th: ({ children }) => (
        <th className="text-icoGray-800 max-w-[10rem] whitespace-normal px-3 py-2.5 text-left font-GSemiBold tracking-tight first:pl-3 last:pr-3 dark:text-white">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="min-w-0 max-w-xs break-words px-3 py-2.5 align-top text-icoBlack first:pl-3 last:pr-3 dark:text-white sm:max-w-sm">
            {children}
        </td>
    ),
    code: ({ className, children, ...props }) =>
        SparksAgentService.isFenceCodeClass(className) ? (
            <code
                className={classNames(
                    className,
                    'font-mono block text-[11px] leading-relaxed tracking-normal text-icoBlack dark:text-white'
                )}
                {...props}>
                {children}
            </code>
        ) : (
            <code
                className="font-mono inline-block max-w-full whitespace-pre-wrap break-words rounded bg-icoGray-100 px-1 py-0.5 text-[11px] dark:bg-icoDarkMode-deepBlack dark:text-white"
                {...props}>
                {children}
            </code>
        )
} as ComponentProps<typeof ReactMarkdown>['components'];

export function SparksMessageBubble({
    message,
    animateTyping = false,
    onTypingComplete
}: SparksMessageBubbleProps) {
    const { t } = useTranslation('sparks');
    const { copy, copied } = useCopyToClipboard();
    const isUser = message.role === 'user';
    const hasSources = Boolean(message.tool_results?.length);

    const shouldAnimateAgent =
        !isUser && Boolean(animateTyping && message.content.trim().length > 0);

    const { displayedText, isComplete } = useAgentMessageTypewriter(
        message.content,
        shouldAnimateAgent,
        { onComplete: onTypingComplete }
    );

    const handleCopy = () => {
        if (message.content) copy(message.content);
    };

    const bubbleContent = isUser ? (
        <span className="min-w-0 whitespace-pre-wrap break-words">{message.content}</span>
    ) : message.content ? (
        <div className="flex min-w-0 max-w-full items-end gap-1">
            <div className="min-w-0 max-w-full flex-1 overflow-x-hidden">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                    {displayedText}
                </ReactMarkdown>
            </div>
            {shouldAnimateAgent && !isComplete && (
                <span
                    className="mb-0.5 inline-block h-3 w-px shrink-0 animate-pulse bg-icoGray-400 dark:bg-icoDarkMode-wolf"
                    aria-hidden
                />
            )}
        </div>
    ) : (
        <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">—</span>
    );

    return (
        <div
            className={classNames(
                'min-w-0 rounded-xl px-3 py-2 font-GMedium text-xs leading-5 tracking-tight',
                isUser
                    ? 'ml-auto w-fit max-w-[85%] whitespace-normal break-words bg-icoGray-300 text-icoBlack dark:bg-icoDarkMode-beton dark:text-white'
                    : 'max-w-full overflow-x-hidden whitespace-normal break-words bg-white text-icoBlack dark:bg-icoDarkMode-deepBlack dark:text-white'
            )}>
            {bubbleContent}

            {hasSources && !isUser && (!shouldAnimateAgent || isComplete) && (
                <div className="border-icoGray-200 mt-6 border-t pt-6 dark:border-icoDarkMode-beton">
                    <Disclosure defaultOpen={false}>
                        {({ open }) => (
                            <div className="min-w-0">
                                <DisclosureButton
                                    type="button"
                                    className="flex w-fit max-w-full items-center justify-between gap-2 rounded-md py-1 text-left outline-none ring-icoBlue focus-visible:ring-2 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-icoDarkMode-anthracite">
                                    <span className="flex min-w-0 items-center gap-2">
                                        <span className="text-base leading-none" aria-hidden>
                                            📖
                                        </span>
                                        <span className="font-GSemiBold text-[10px] uppercase leading-3 tracking-wide text-icoGray-500 dark:text-icoDarkMode-wolf">
                                            {t('results_based_on')}
                                        </span>
                                    </span>
                                    <Icon
                                        icon={open ? 'chevron-top' : 'chevron-bottom'}
                                        className="size-3.5 shrink-0 text-icoGray-500 dark:text-icoDarkMode-wolf"
                                        aria-hidden
                                    />
                                </DisclosureButton>
                                <Transition
                                    show={open}
                                    enter="transition-all duration-200 ease-out"
                                    enterFrom="transform grid-rows-[0fr] opacity-0"
                                    enterTo="transform grid-rows-[1fr] opacity-100"
                                    leave="transition-all duration-150 ease-in"
                                    leaveFrom="transform grid-rows-[1fr] opacity-100"
                                    leaveTo="transform grid-rows-[0fr] opacity-0">
                                    <div className="grid">
                                        <DisclosurePanel className="overflow-hidden">
                                            <div className="flex flex-wrap gap-1 pt-2">
                                                {message.tool_results!.map((tool) => (
                                                    <span
                                                        key={tool.id}
                                                        className="text-icoGray-600 rounded-md bg-icoGray-100 px-2 py-0.5 font-GMedium text-[10px] dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf">
                                                        {tool.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </DisclosurePanel>
                                    </div>
                                </Transition>
                            </div>
                        )}
                    </Disclosure>
                </div>
            )}

            {!isUser && message.content && (!shouldAnimateAgent || isComplete) && (
                <button
                    type="button"
                    onClick={handleCopy}
                    aria-label={copied ? t('copied') : t('copy_response')}
                    className="mt-4 flex size-6 shrink-0 items-center justify-center rounded text-icoGray-500 transition-colors hover:text-icoBlack dark:text-icoDarkMode-wolf dark:hover:text-white">
                    <Icon icon={copied ? 'check' : 'copy-outline'} className="text-[14px]" />
                </button>
            )}
        </div>
    );
}
